import URLParamService from "./URLParamService";
import { hasOwn } from "@/utils/object";

const queryParams = URLParamService.getQueryParams();
const promoId = queryParams.promoId;
const sessionId = queryParams.session;
const lng = queryParams.lng;
const gameBaseUrl = queryParams.gameUrl;
const imageBaseUrl = queryParams.imageUrl;
const gameId = queryParams.gameId;
const providerId = queryParams.providerId;
const showGameImages = queryParams.showGameImages;
const enableGameNavigation = queryParams.enableGameNavigation;
const gameClickMode = queryParams.gameClickMode;
const playClickMode = queryParams.playClickMode;
const showInactiveBetPopup = queryParams.showInactiveBetPopup;
const supportPhone = queryParams.supportPhone;
const hideLoader = queryParams.hideLoader;
const origin = queryParams.origin;
const cdnBaseURL = import.meta.env.VITE_WIDGET_CDN_BASE_URL;

const isPromoValid = (): boolean => {
  if (hasOwn(queryParams, "promoId") && promoId) {
    return true;
  }
  return false;
};

const getPromoId = (): string => {
  if (hasOwn(queryParams, "promoId") && promoId?.length > 0) {
    return promoId;
  }
  return "";
};
const getSession = (): string => {
  if (hasOwn(queryParams, "session") && sessionId?.length > 0) {
    return sessionId;
  }
  return "undefined";
};

const getLng = (): string => {
  if (hasOwn(queryParams, "lng") && lng?.length > 0) {
    return lng;
  }
  return "";
};

const getGameBaseUrl = (): string => {
  return hasOwn(queryParams, "gameUrl") ? gameBaseUrl : "";
};

const getImageBaseUrl = (): string => {
  return hasOwn(queryParams, "imageUrl") ? imageBaseUrl : "";
};

const getShowGameImages = (): boolean => {
  if (hasOwn(queryParams, "showGameImages")) {
    return showGameImages === "true" || showGameImages === "1";
  }
  return true;
};

const getEnableGameNavigation = (): boolean => {
  if (hasOwn(queryParams, "enableGameNavigation")) {
    return enableGameNavigation === "true" || enableGameNavigation === "1";
  }
  return true;
};

const isGameClickEventMode = (): boolean => gameClickMode === "event";

const isPlayClickEventMode = (): boolean => playClickMode === "event";

const getShowErrorBlockPopup = (): boolean => {
  if (hasOwn(queryParams, "showInactiveBetPopup")) {
    return showInactiveBetPopup === "true" || showInactiveBetPopup === "1";
  }
  return false;
};

const getHideLoader = (): boolean => hideLoader === "true" || hideLoader === "1";

const getSupportPhone = (): string => {
  return hasOwn(queryParams, "supportPhone") ? supportPhone : "";
};

// Backend origin filtering matches bare hostnames (the admin panel stores
// origins like "gagna.ge", no scheme), so detected values are reduced to
// their hostname.
const toHostname = (value: string): string => {
  try {
    return new URL(value).hostname;
  } catch {
    return "";
  }
};

// Auto-detected embedding-page origin, used only when no explicit `origin`
// query param is provided (native apps loading the widget directly have no
// parent and must pass the param). Only applies when actually iframed —
// top-level document.referrer is a navigation referrer, not a parent.
const getParentOrigin = (): string => {
  if (window.self === window.top) return "";

  // Browser-verified immediate parent; missing on Firefox.
  const ancestorOrigin = window.location.ancestorOrigins?.[0];
  if (ancestorOrigin) {
    const host = toHostname(ancestorOrigin);
    if (host) return host;
  }

  // Fallback — can be stripped by the parent site's Referrer-Policy.
  if (document.referrer) {
    return toHostname(document.referrer);
  }
  return "";
};

const getOrigin = (): string => {
  if (hasOwn(queryParams, "origin") && origin?.length > 0) {
    // Normalize a full-URL param to the same bare-hostname format the
    // auto-detected branch produces; a bare hostname passes through as-is.
    return toHostname(origin) || origin;
  }
  return getParentOrigin();
};

const toOrigin = (value: string): string => {
  try {
    const { origin: parsedOrigin } = new URL(value);
    return parsedOrigin === "null" ? "" : parsedOrigin;
  } catch {
    return "";
  }
};

const getHostOrigin = (): string => {
  if (origin?.length > 0) {
    const hasScheme = /^[a-z][a-z\d+.-]*:\/\//i.test(origin);
    const explicitOrigin = toOrigin(hasScheme ? origin : `https://${origin}`);
    if (explicitOrigin) return explicitOrigin;
  }

  if (window.self === window.top) return "";

  const ancestorOrigins = window.location.ancestorOrigins;
  const topAncestorOrigin = ancestorOrigins?.[ancestorOrigins.length - 1];
  if (topAncestorOrigin && topAncestorOrigin !== "null") return topAncestorOrigin;

  const referrerOrigin = document.referrer ? toOrigin(document.referrer) : "";
  return referrerOrigin !== window.location.origin ? referrerOrigin : "";
};

const getGameId = (): string => {
  if (hasOwn(queryParams, "gameId") && gameId?.length > 0) {
    return gameId;
  }
  return "";
};

const getProviderId = (): string => {
  if (hasOwn(queryParams, "providerId") && providerId?.length > 0) {
    return providerId;
  }
  return "";
};

const ConfigService = {
  cdnBaseURL,
  isPromoValid,
  getPromoId,
  getGameId,
  getProviderId,
  getSession,
  getLng,
  getGameBaseUrl,
  getImageBaseUrl,
  getShowGameImages,
  getEnableGameNavigation,
  isGameClickEventMode,
  isPlayClickEventMode,
  getShowErrorBlockPopup,
  getSupportPhone,
  getHideLoader,
  getOrigin,
  getHostOrigin,
};

export default ConfigService;
